<script setup lang="ts">
  import { useInfiniteScroll } from '@vueuse/core';
  import { computed, ref } from 'vue';
  import type { Resort } from '@/shared/model';
  import DestinationMobileCheckRow from './DestinationMobileCheckRow.vue';
  import DestinationMobileEmpty from './DestinationMobileEmpty.vue';
  import DestinationMobileSection from './DestinationMobileSection.vue';
  import { DESTINATION_RESORTS_LOAD_DISTANCE, DESTINATION_RESORTS_PAGE_SIZE } from '../config/mobileList';
  import { getResortGroups } from '../lib/resortGroups';
  import type { ResortGroupTitles } from '../model/types';

  interface DestinationResortListProps {
    resorts: Resort[];
    checkedIds: number[];
    allLabel: string;
    allChecked: boolean;
    query?: string;
    groupTitles?: ResortGroupTitles;
    divided?: boolean;
  }

  const props = withDefaults(defineProps<DestinationResortListProps>(), {
    query: '',
    groupTitles: undefined,
    divided: true,
  });

  interface DestinationResortListEmits {
    (e: 'toggle', resort: Resort): void;
    (e: 'toggle-all'): void;
  }

  const emit = defineEmits<DestinationResortListEmits>();

  interface ResortRow {
    resort: Resort;
    key: string;
  }

  interface ResortRowGroup {
    title: string | null;
    rows: ResortRow[];
  }

  const hasQuery = computed<boolean>(() => props.query.trim() !== '');

  const checkedIdSet = computed<Set<number>>(() => new Set(props.checkedIds));

  const rowGroups = computed<ResortRowGroup[]>(() =>
    getResortGroups(props.resorts, props.query, props.groupTitles).map((group) => ({
      title: group.title,
      rows: group.items.flatMap((item) =>
        item.type === 'resort' ? [{ resort: item.resort, key: `resort-${item.resort.id}` }] : [],
      ),
    })),
  );

  const isEmpty = computed<boolean>(() => hasQuery.value && rowGroups.value.length === 0);

  const rootRef = ref<HTMLElement | null>(null);
  const scrollContainer = computed(() => rootRef.value?.parentElement ?? null);

  const rows = computed<ResortRow[]>(() => rowGroups.value.flatMap((group) => group.rows));

  const visibleRowsCount = ref(DESTINATION_RESORTS_PAGE_SIZE);

  const visibleRowGroups = computed<ResortRowGroup[]>(() => {
    let restCount = visibleRowsCount.value;
    return rowGroups.value.flatMap((group) => {
      if (restCount <= 0) {
        return [];
      }
      const visibleRows = group.rows.slice(0, restCount);
      restCount -= visibleRows.length;
      return [{ ...group, rows: visibleRows }];
    });
  });

  useInfiniteScroll(
    scrollContainer,
    () => {
      visibleRowsCount.value += DESTINATION_RESORTS_PAGE_SIZE;
    },
    {
      distance: DESTINATION_RESORTS_LOAD_DISTANCE,
      canLoadMore: () => visibleRowsCount.value < rows.value.length,
    },
  );
</script>

<template>
  <div
    ref="rootRef"
    class="destination-resort-list"
    data-testid="destination-resort-list"
  >
    <template v-if="!hasQuery">
      <DestinationMobileCheckRow
        :name="allLabel"
        :checked="allChecked"
        data-testid="destination-mobile-select-all"
        @toggle="emit('toggle-all')"
      />
      <div
        v-if="divided"
        class="destination-resort-list__divider"
        data-testid="destination-resort-list-divider"
      />
    </template>
    <DestinationMobileEmpty v-if="isEmpty" />

    <template v-for="group in visibleRowGroups">
      <DestinationMobileSection
        v-if="group.title"
        :key="`resort-title-${group.title}`"
        :title="group.title ?? ''"
      />
      <DestinationMobileCheckRow
        v-for="row in group.rows"
        :key="row.key"
        :data-anchor="row.key"
        :name="row.resort.name"
        :checked="checkedIdSet.has(row.resort.id)"
        :highlight="query"
        @toggle="emit('toggle', row.resort)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
  .destination-resort-list {
    &__divider {
      background: var(--color-bg-secondary);
      height: 1px;
      margin: var(--spacing-xs) var(--spacing-m);
    }
  }
</style>
